import { useEffect, useState } from "react";
import { useSearch } from "@tanstack/react-router";
import Cookies from "js-cookie";
import { Text } from "styles/utility-components";

import {
  CHANGE_EMAIL_REQUEST_ERRORS,
  PENDING_EMAIL_CHANGE_STORAGE_KEY,
  PendingEmailChange,
} from "components/account_management/account/ChangeEmail";
import Button from "components/ui/button/Button";
import { useLocalStorage } from "lib/hooks/useLocalStorage";
import { Card, Page } from "./authPageStyles";
import { StotlesLogo } from "./StotlesLogo";

/**
 * Set by the auth-service on the redirect from a link that went through: `confirmed` for the
 * old-address link, `complete` for the new-address one. Named like better-auth's own cookies —
 * `__Secure-stotles-auth.` over https (prod), `stotles-auth.` locally. Keep in sync with
 * EMAIL_CHANGE_RESULT_COOKIE in auth-service's email-change-restrictions.ts.
 */
const EMAIL_CHANGE_RESULT_COOKIE = "stotles-auth.email_change_result";
const SECURE_EMAIL_CHANGE_RESULT_COOKIE = `__Secure-${EMAIL_CHANGE_RESULT_COOKIE}`;

type EmailChangeResult = "confirmed" | "complete";

// Codes better-auth's /verify-email and auth-service's email-change rules append as `?error=`,
// matched case-insensitively.
const EMAIL_CHANGE_ERRORS: Record<string, string> = {
  token_expired:
    "That link has expired. Start the email change process again from your account settings.",
  invalid_token:
    "That link isn't valid. It may have been cut off or altered, so try copying the full link from the email.",
  // The token is keyed to the old address, so once the change has gone through the lookup misses.
  user_not_found:
    "We couldn't find an account for that link. Your email may already have been updated.",
  invalid_user: "That link was issued for a different account. Sign out first, then open it again.",
  // These errors shouldn't happen as the change request runs the same checks but have here just in case.
  ...CHANGE_EMAIL_REQUEST_ERRORS,
  // Only from the new-address link, whose owner already knows the address is theirs.
  email_already_in_use: "Another account already uses that email address.",
};

const DEFAULT_EMAIL_CHANGE_ERROR = "We couldn't change your email from that link.";

/** Copy for a failed email-change link. Unknown codes fall back to a generic message. */
export function emailChangeErrorText(error: string): string {
  return EMAIL_CHANGE_ERRORS[error.toLowerCase()] ?? DEFAULT_EMAIL_CHANGE_ERROR;
}

function readResultCookie(): EmailChangeResult | null {
  // Same lookup better-auth uses for its own cookies: the secure name first.
  const value =
    Cookies.get(SECURE_EMAIL_CHANGE_RESULT_COOKIE) ?? Cookies.get(EMAIL_CHANGE_RESULT_COOKIE);
  return value === "confirmed" || value === "complete" ? value : null;
}

function clearResultCookie() {
  // A `__Secure-` cookie can only be touched with `Secure`, so each name is cleared separately.
  Cookies.remove(SECURE_EMAIL_CHANGE_RESULT_COOKIE, { path: "/", secure: true });
  Cookies.remove(EMAIL_CHANGE_RESULT_COOKIE, { path: "/" });
}

/**
 * Landing page for both links of an email change, on whichever device the email was opened.
 *
 * Success comes only from the auth-service's result cookie, never the URL: a crafted link could
 * otherwise make our own domain tell someone their email had changed. Without the cookie the page
 * shows `?error=` if there is one, or nothing specific.
 */
export function BetterAuthEmailChangePage() {
  // `strict: false` lets this resolve to undefined when rendered outside the route (e.g. tests).
  const { error } = useSearch({ strict: false });

  // Set by the auth-service on whichever browser opens a link. Read once on mount: it's cleared
  // straight after, so a refresh shows the neutral state.
  const [result] = useState(readResultCookie);
  // Only set in the browser that requested the change, so it can be absent.
  const [pendingEmailChange, setPendingEmailChange] = useLocalStorage<PendingEmailChange | null>(
    PENDING_EMAIL_CHANGE_STORAGE_KEY,
    null,
  );
  const pendingEmail =
    pendingEmailChange && pendingEmailChange.expiresAt > Date.now()
      ? pendingEmailChange.email
      : null;

  useEffect(() => {
    // Doesn't work if the auth-service set the cookie on a different domain (sandbox only).
    // It expires after a minute anyway, so leaving it behind does little harm.
    clearResultCookie();
  }, []);

  useEffect(() => {
    // If this browser requested the change, local storage is also used to remember that to show
    // a nicer UI in the change password settings page but it should be cleared once change is done.
    if (result === "complete") setPendingEmailChange(null);
  }, [result, setPendingEmailChange]);

  if (result === "complete") {
    return (
      <EmailChangeCard title="Your email address has been changed">
        <Text color="sysTextSecondary">Use your new email address to sign in from now on.</Text>
        {/* A full navigation, so the app boots with the session this link just signed you in to. */}
        <Button buttonType="primary" fullWidth onClick={() => window.location.assign("/")}>
          Continue to Stotles
        </Button>
      </EmailChangeCard>
    );
  }

  if (result === "confirmed") {
    return (
      <EmailChangeCard title="Check your new inbox">
        <Text color="sysTextSecondary">
          Thanks for approving the change. We&apos;ve sent an email to{" "}
          {pendingEmail ? <strong>{pendingEmail}</strong> : "your new address"} — open the link in
          it to finish changing your email.
        </Text>
      </EmailChangeCard>
    );
  }

  if (error) {
    return (
      <EmailChangeCard title="We couldn't change your email">
        <Text color="sysTextSecondary">{emailChangeErrorText(error)}</Text>
        <SettingsLink />
      </EmailChangeCard>
    );
  }

  return (
    <EmailChangeCard title="Change your email">
      <Text color="sysTextSecondary">
        You can change the email address on your account from your account settings.
      </Text>
      <SettingsLink />
    </EmailChangeCard>
  );
}

function EmailChangeCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Page>
      <Card centred>
        <StotlesLogo />
        <Text h1>{title}</Text>
        {children}
      </Card>
    </Page>
  );
}

function SettingsLink() {
  return (
    <Button as="a" to="/account-management/account" buttonType="primary" fullWidth>
      Go to account settings
    </Button>
  );
}
